import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import ReservationService from '../../services/reservation';
import ScheduleService from '../../services/schedule';
import moment from 'moment';
import personIcon from '../../img/new_person_icon.svg';
import _ from 'lodash';
import config from '../../config';
import { history as browserHistory } from '../../helpers/history';
import { eventTracker } from '../../helpers/ga_events';
import SeatMap from '../SeatSelection/SeatMap';
import HoldTimer from '../SeatSelection/HoldTimer';
import SessionExpiredPopup from '../SeatSelection/SessionExpiredPopup';
import UserStore from '../../stores/UserStore';
import getSessionId from '../../helpers/sessionId';
import InputMask from 'react-input-mask';
import '../SeatSelection/SeatSelection.scss';

function formatErrorBody(body) {
  if (!body || typeof body !== 'object' || !body.message) return null;
  var msg = body.message;
  if (typeof body.amountDue === 'number' && body.amountDue > 0) {
    msg += '. Amount due: $' + body.amountDue.toFixed(2);
    if (typeof body.availableCredit === 'number' && body.availableCredit > 0) {
      msg += ' ($' + body.availableCredit.toFixed(2) + ' account credit available)';
    }
    msg += '. Seat prices may have changed since this page loaded — please refresh and try again.';
  }
  return msg;
}

function friendlyError(err) {
  var response = err && err.response;
  if (!response) return 'An error occurred';
  var msg = formatErrorBody(response.body);
  if (msg) return msg;
  var text = response.text;
  if (typeof text === 'string' && text) {
    if (text.charAt(0) === '{') {
      try {
        msg = formatErrorBody(JSON.parse(text));
        if (msg) return msg;
      } catch (e) { /* not JSON after all — fall through */ }
    } else {
      return text;
    }
  }
  return response.statusText || 'An error occurred';
}

function getSeatTierAddon(seatId, tiers) {
  if (!seatId || !tiers) return 0;
  var rowMatch = seatId.match(/^(\d+)/);
  if (!rowMatch) return 0;
  var rowNumber = parseInt(rowMatch[1], 10);
  for (var i = 0; i < tiers.length; i++) {
    var tier = tiers[i];
    if (tier.rows && tier.rows.indexOf(rowNumber) !== -1) {
      return tier.additionalPrice || 0;
    }
  }
  return 0;
}

function paidSeatValue(res, seatId, tiers) {
  // seatTierPrice from the API is what was actually paid; 0 is a valid value, so a truthiness check must not skip it
  return res && typeof res.seatTierPrice === 'number' ? res.seatTierPrice : getSeatTierAddon(seatId, tiers);
}

class Reservation extends React.Component {
  constructor(props) {
    super(props);

    const reservations = (props.reservation && props.reservation.reservations) || [];
    const hasSeatNumbers = reservations.some(function(r) { return !!r.seat_number; });

    this.state = {
      cities: config.cities,
      loading: false,
      error: '',
      success: '',
      count: hasSeatNumbers ? 0 : 1,
      seats: [],
      hasSeatNumbers: hasSeatNumbers,
      seatsToCancel: [],
      busLayout: null,
      takenSeats: [],
      seatInfoLoading: false,
      seatInfoError: null,
      maxSelectableRow: null,
      holdTimerKey: 0,
      sessionExpired: false,
      showSeatModal: false,
      modalAction: null,
      // Switch seat
      switchStep: 1,
      seatsToSwitch: [],
      switchMappings: {},
      switchActiveIndex: 0,
      switchPendingSeats: [],
      switchPendingReleaseSeats: [],
      pricingTiers: [],
      // Payment
      payWithCard: false,
      cardHolder: '',
      cardNumber: '',
      cardMonth: '',
      cardYear: '',
      cardCvv: '',
      cardZip: '',
      card_cardHolder_error: '',
      card_cardNumber_error: '',
      card_cardMonth_error: '',
      card_cardYear_error: '',
      card_cardCvv_error: '',
      card_cardZip_error: '',
    };
  }

  handleCardChange(e) {
    var update = {};
    update[e.target.name] = e.target.value;
    update['card_' + e.target.name + '_error'] = '';
    this.setState(update);
  }

  handleCardBlur(e) {
    var name = e.target.name;
    var value = e.target.value;
    var errorKey = 'card_' + name + '_error';
    var update = {};

    if (!value) {
      update[errorKey] = 'Required';
    } else if (name === 'cardCvv' && /\d{3}/.test(value) === false && /\d{4}/.test(value) === false) {
      update[errorKey] = 'Invalid';
    } else if (name === 'cardZip' && /\d{5}([\-]\d{4})?/.test(value) === false) {
      update[errorKey] = 'Invalid';
    } else if (name === 'cardHolder' && /^([A-Za-z\s.]{3,})$/.test(value) === false) {
      update[errorKey] = 'Invalid';
    } else if (name === 'cardNumber' && /\d{4}[\s.-]\d{4}[\s.-]\d{4}[\s.-]\d{4}/.test(value) === false && /\d{4}[\s.-]\d{4}[\s.-]\d{4}[\s.-]\d{3}/.test(value) === false) {
      update[errorKey] = 'Invalid';
    } else {
      update[errorKey] = '';
    }

    this.setState(update);
  }

  validateCard() {
    var errors = {};
    var valid = true;

    if (!this.state.cardHolder) { errors.card_cardHolder_error = 'Required'; valid = false; }
    else if (/^([A-Za-z\s.]{3,})$/.test(this.state.cardHolder) === false) { errors.card_cardHolder_error = 'Invalid'; valid = false; }

    if (!this.state.cardNumber) { errors.card_cardNumber_error = 'Required'; valid = false; }
    else if (/\d{4}[\s.-]\d{4}[\s.-]\d{4}[\s.-]\d{4}/.test(this.state.cardNumber) === false && /\d{4}[\s.-]\d{4}[\s.-]\d{4}[\s.-]\d{3}/.test(this.state.cardNumber) === false) { errors.card_cardNumber_error = 'Invalid'; valid = false; }

    if (!this.state.cardMonth) { errors.card_cardMonth_error = 'Required'; valid = false; }
    if (!this.state.cardYear) { errors.card_cardYear_error = 'Required'; valid = false; }

    if (!this.state.cardCvv) { errors.card_cardCvv_error = 'Required'; valid = false; }
    else if (/\d{3}/.test(this.state.cardCvv) === false && /\d{4}/.test(this.state.cardCvv) === false) { errors.card_cardCvv_error = 'Invalid'; valid = false; }

    if (!this.state.cardZip) { errors.card_cardZip_error = 'Required'; valid = false; }
    else if (/\d{5}([\-]\d{4})?/.test(this.state.cardZip) === false) { errors.card_cardZip_error = 'Invalid'; valid = false; }

    if (!valid) this.setState(errors);
    return valid;
  }

  openSeatModal(action) {
    this.setState({
      showSeatModal: true,
      modalAction: action,
      seatsToCancel: [],
      cancelUnassignedCount: 0,
      count: 0,
      error: '',
      switchStep: 1,
      seatsToSwitch: [],
      switchMappings: {},
      switchActiveIndex: 0,
      switchPendingSeats: [],
      switchPendingReleaseSeats: [],
      payWithCard: false, cardHolder: '', cardNumber: '', cardMonth: '', cardYear: '', cardCvv: '', cardZip: '',
      card_cardHolder_error: '', card_cardNumber_error: '', card_cardMonth_error: '', card_cardYear_error: '', card_cardCvv_error: '', card_cardZip_error: '',
    });
  }

  closeSeatModal() {
    ScheduleService.releaseAllHolds(getSessionId()).catch(function() {});
    this.setState({
      showSeatModal: false,
      modalAction: null,
      loading: false,
      loadingMessage: '',
      modalLoading: false,
      error: '',
      seatsToCancel: [],
      cancelUnassignedCount: 0,
      switchStep: 1,
      seatsToSwitch: [],
      switchMappings: {},
      switchActiveIndex: 0,
      switchPendingSeats: [],
      switchPendingReleaseSeats: [],
      sessionExpired: false,
    });
  }

  handleHoldTimerExpired = () => {
    ScheduleService.releaseAllHolds(getSessionId()).catch(function() {});
    this.setState({
      showSeatModal: false,
      modalAction: null,
      switchStep: 1,
      seatsToSwitch: [],
      switchMappings: {},
      switchActiveIndex: 0,
      switchPendingSeats: [],
      switchPendingReleaseSeats: [],
      sessionExpired: true,
    });
  }

  handleSessionRestart = () => {
    this.setState({ sessionExpired: false });
    this.fetchSeatInfo();
  }

  confirmFromModal() {
    var action = this.state.modalAction;
    var self = this;
    if (action === 'cancelAddon') {
      self.proceedCancelAddon();
      return;
    }
    this.setState({ showSeatModal: false, modalAction: null }, function() {
      if (action === 'cancel') {
        self.cancelSeats();
      } else if (action === 'reschedule') {
        self.rescheduleSeats();
      }
    });
  }

  handleCancelClick() {
    if (this.state.hasSeatNumbers) {
      var reservations = this.props.reservation.reservations || [];
      var activeAll = reservations.filter(function(r) { return r.status === 'active'; });
      if (activeAll.length <= 1) {
        var allSeats = activeAll.filter(function(r) { return r.seat_number; }).map(function(r) { return r.seat_number; });
        var unassignedCount = activeAll.filter(function(r) { return !r.seat_number; }).length;
        this.setState({ seatsToCancel: allSeats, cancelUnassignedCount: unassignedCount, count: activeAll.length }, function() {
          this.cancelSeats();
        });
        return;
      }
      this.openSeatModal('cancel');
    } else {
      this.cancelSeats();
    }
  }

  handleRescheduleClick() {
    if (this.state.hasSeatNumbers) {
      this.openSeatModal('reschedule');
    } else {
      this.rescheduleSeats();
    }
  }

  handleCancelAddonClick() {
    this.openSeatModal('cancelAddon');
  }

  handleSwitchSeatClick() {
    this.openSeatModal('switch');
    this.fetchSeatInfo();
  }

  handleAddSeatClick() {
    var self = this;
    var reservations = this.props.reservation.reservations || [];
    var ticketCount = reservations.filter(function(r) { return !r.seat_number; }).length;
    if (!ticketCount) return;
    // Assign only as many seats as the count selector asks for (the modal requires every
    // placeholder to be mapped before it proceeds)
    var requestedCount = parseInt(this.state.count, 10) || ticketCount;
    var seatsToAssign = Math.min(requestedCount, ticketCount);
    var placeholders = [];
    for (var i = 0; i < seatsToAssign; i++) { placeholders.push('ticket_' + i); }
    var initMappings = {};
    placeholders.forEach(function(p) { initMappings[p] = null; });
    this.setState({
      showSeatModal: true,
      modalAction: 'addSeat',
      error: '',
      switchStep: 2,
      seatsToSwitch: placeholders,
      switchMappings: initMappings,
      switchActiveIndex: 0,
      holdTimerKey: this.state.holdTimerKey + 1,
      cardHolder: '', cardNumber: '', cardMonth: '', cardYear: '', cardCvv: '', cardZip: '',
    }, function() {
      self.fetchSeatInfo();
    });
  }

  proceedCancelAddon() {
    if (!this.state.seatsToCancel.length) return;
    var seatsToCancel = this.state.seatsToCancel.slice();
    this.setState({ loading: true, loadingMessage: 'Removing seat selection...', showSeatModal: false, error: '', success: '' });
    var self = this;
    ReservationService.cancelSeatAddon(
      this.props.reservation.reservationNumber,
      this.props.reservation.email,
      seatsToCancel,
      this.props.reservation.reservationGroup
    )
    .then(function(result) {
      eventTracker('cancel_seat_addon', 'click', 'Reservations');
      var details = encodeURIComponent(JSON.stringify({
        seats: seatsToCancel,
        credit: (result.refund && result.refund.credit) || 0,
        email: self.props.reservation.email,
      }));
      var resNum = self.props.reservation.reservationNumber;
      window.location.href = '/edit-trip?reservationNumber=' + resNum + '&seatAction=cancelAddon&details=' + details;
    })
    .catch(function(err) {
      self.setState({ loading: false, loadingMessage: '', error: friendlyError(err) });
    });
  }

  handleSwitchSelectToggle = (seatId) => {
    var userSeats = this.props.reservation.reservations
      .map(function(r) { return r.seat_number; })
      .filter(Boolean);
    if (userSeats.indexOf(seatId) === -1) return;
    this.setState(function(prevState) {
      var seats = prevState.seatsToSwitch;
      var updated;
      if (seats.indexOf(seatId) !== -1) {
        updated = seats.filter(function(s) { return s !== seatId; });
      } else {
        updated = seats.concat([seatId]);
      }
      return { seatsToSwitch: updated };
    });
  }

  handleSwitchMapSeat = (seatId) => {
    var self = this;
    var seatsToSwitch = this.state.seatsToSwitch;
    var currentSeat = seatsToSwitch[this.state.switchActiveIndex];
    if (!currentSeat) return;
    var userSeats = this.props.reservation.reservations
      .map(function(r) { return r.seat_number; })
      .filter(Boolean);
    if (userSeats.indexOf(seatId) !== -1) return;
    var mappings = this.state.switchMappings;

    if (this.state.switchPendingSeats.length > 0 || this.state.switchPendingReleaseSeats.length > 0) return;

    // If clicking an already-selected seat, find which mapping owns it and remove
    var ownerKey = null;
    Object.keys(mappings).forEach(function(k) { if (mappings[k] === seatId) ownerKey = k; });
    if (ownerKey) {
      this.removeSwitchMapping(ownerKey);
      return;
    }

    var alreadyMapped = [];
    Object.keys(mappings).forEach(function(k) { if (mappings[k] && k !== currentSeat) alreadyMapped.push(mappings[k]); });
    if (alreadyMapped.indexOf(seatId) !== -1) return;

    var previousTarget = mappings[currentSeat];

    this.setState(function(prevState) {
      var newMappings = {};
      Object.keys(prevState.switchMappings).forEach(function(k) { newMappings[k] = prevState.switchMappings[k]; });
      newMappings[currentSeat] = seatId;
      var nextIndex = prevState.switchActiveIndex;
      for (var i = 1; i <= seatsToSwitch.length; i++) {
        var idx = (prevState.switchActiveIndex + i) % seatsToSwitch.length;
        if (!newMappings[seatsToSwitch[idx]]) {
          nextIndex = idx;
          break;
        }
      }
      return { switchMappings: newMappings, switchActiveIndex: nextIndex, switchPendingSeats: prevState.switchPendingSeats.concat([seatId]) };
    });

    var scheduleBus = this.props.reservation.schedule_bus;
    var slot = this.props.reservation.reservations[0].slot;
    var sessionId = getSessionId();

    if (previousTarget && previousTarget !== seatId) {
      ScheduleService.releaseSeat(scheduleBus, slot, previousTarget, sessionId).catch(function() {});
    }

    ScheduleService.holdSeat(scheduleBus, slot, seatId, sessionId)
      .then(function() {
        self.setState(function(prevState) {
          return { switchPendingSeats: prevState.switchPendingSeats.filter(function(s) { return s !== seatId; }) };
        });
      })
      .catch(function(err) {
        if (err && err.conflict) {
          var freshTempSeats = err.temporaryTakenSeats || null;
          var freshTakenSeats = err.takenSeats || null;
          self.setState(function(prevState) {
            var newMappings = {};
            Object.keys(prevState.switchMappings).forEach(function(k) { newMappings[k] = prevState.switchMappings[k]; });
            newMappings[currentSeat] = null;
            var update = {
              switchMappings: newMappings,
              error: 'Seat ' + seatId + ' is no longer available. Please pick another.',
              switchPendingSeats: prevState.switchPendingSeats.filter(function(s) { return s !== seatId; }),
            };
            if (freshTempSeats) update.temporaryTakenSeats = freshTempSeats;
            if (freshTakenSeats) update.takenSeats = freshTakenSeats;
            return update;
          });
          if (!freshTempSeats && !freshTakenSeats) {
            self.fetchSeatInfo();
          }
        } else {
          self.setState(function(prevState) {
            return { switchPendingSeats: prevState.switchPendingSeats.filter(function(s) { return s !== seatId; }) };
          });
        }
      });
  }

  removeSwitchMapping = (seatKey) => {
    var self = this;
    var target = this.state.switchMappings[seatKey];

    this.setState(function(prevState) {
      var newMappings = {};
      Object.keys(prevState.switchMappings).forEach(function(k) { newMappings[k] = prevState.switchMappings[k]; });
      newMappings[seatKey] = null;
      var seatsToSwitch = prevState.seatsToSwitch;
      var newActiveIndex = seatsToSwitch.indexOf(seatKey);
      if (newActiveIndex === -1) newActiveIndex = 0;
      var update = { switchMappings: newMappings, switchActiveIndex: newActiveIndex, error: '' };
      if (target) {
        update.switchPendingReleaseSeats = prevState.switchPendingReleaseSeats.concat([target]);
      }
      return update;
    });

    if (target) {
      var scheduleBus = this.props.reservation.schedule_bus;
      var slot = this.props.reservation.reservations[0].slot;
      ScheduleService.releaseSeat(scheduleBus, slot, target, getSessionId())
        .then(function() {
          self.setState(function(prevState) {
            return { switchPendingReleaseSeats: prevState.switchPendingReleaseSeats.filter(function(s) { return s !== target; }) };
          });
          self.fetchSeatInfo();
        })
        .catch(function(err) {
          console.error('[releaseSeat switch]', err);
          self.setState(function(prevState) {
            return { switchPendingReleaseSeats: prevState.switchPendingReleaseSeats.filter(function(s) { return s !== target; }) };
          });
        });
    }
  }

  proceedSwitchSeat() {
    var self = this;
    var seatsToSwitch = this.state.seatsToSwitch;
    var mappings = this.state.switchMappings;
    var tiers = this.state.pricingTiers || [];
    var isAddSeat = this.state.modalAction === 'addSeat';
    var allMapped = seatsToSwitch.every(function(s) { return mappings[s]; });
    if (!allMapped) return;

    var allReservations = this.props.reservation.reservations || [];
    var switches = seatsToSwitch.map(function(seatKey) {
      var newSeat = mappings[seatKey];
      var oldSeat = isAddSeat ? null : seatKey;
      var oldRes = oldSeat ? allReservations.find(function(r) { return r.seat_number === oldSeat; }) : null;
      var oldPrice = paidSeatValue(oldRes, oldSeat, tiers);
      var diff = getSeatTierAddon(newSeat, tiers) - oldPrice;
      return { old_seat_number: oldSeat, new_seat_number: newSeat, diff: diff };
    });

    var user = UserStore.getUser();
    var remainingCredit = (user && user.credit) || 0;
    var payWithCard = this.state.payWithCard;
    var needsCard = false;
    var totalUpgrade = 0;
    seatsToSwitch.forEach(function(oldSeat) {
      var oldRes = allReservations.find(function(r) { return r.seat_number === oldSeat; });
      var oldPrice = paidSeatValue(oldRes, oldSeat, tiers);
      var diff = getSeatTierAddon(mappings[oldSeat], tiers) - oldPrice;
      if (diff > 0) {
        totalUpgrade += diff;
        if (payWithCard) {
          needsCard = true;
        } else {
          var c = Math.min(remainingCredit, diff);
          remainingCredit -= c;
          if (diff - c > 0) needsCard = true;
        }
      } else if (diff < 0) {
        if (!payWithCard) remainingCredit += Math.abs(diff);
      }
    });

    if (needsCard && !this.validateCard()) {
      return;
    }

    var card = needsCard ? {
      cardNumber: this.state.cardNumber.replace(/-/g, ''),
      expiryDate: '' + this.state.cardMonth + this.state.cardYear,
      cvv: this.state.cardCvv,
      zip: this.state.cardZip,
    } : null;

    this.setState({ loading: true, loadingMessage: isAddSeat ? 'Seat assignment in progress...' : 'Seat switch in progress...', showSeatModal: false, error: '', success: '' });

    var scheduleBus = this.props.reservation.schedule_bus;
    var slot = this.props.reservation.reservations[0].slot;
    var sessionId = getSessionId();
    var targetSeats = switches.map(function(s) { return s.new_seat_number; });

    ScheduleService.getSeatInfo(scheduleBus, slot, sessionId)
      .then(function(data) {
        var unavailable = {};
        (data.takenSeats || []).forEach(function(s) { unavailable[s] = true; });
        (data.temporaryTakenSeats || []).forEach(function(s) { unavailable[s] = true; });
        var invalid = targetSeats.filter(function(s) { return unavailable[s]; });
        if (invalid.length > 0) {
          var newMappings = {};
          Object.keys(mappings).forEach(function(k) { newMappings[k] = mappings[k]; });
          invalid.forEach(function(s) {
            Object.keys(newMappings).forEach(function(k) { if (newMappings[k] === s) newMappings[k] = null; });
          });
          self.setState({
            loading: false,
            loadingMessage: '',
            showSeatModal: true,
            switchMappings: newMappings,
            takenSeats: (data && data.takenSeats) || self.state.takenSeats,
            error: 'Seat' + (invalid.length > 1 ? 's' : '') + ' ' + invalid.join(', ') + ' ' + (invalid.length > 1 ? 'are' : 'is') + ' no longer available. Please pick ' + (invalid.length > 1 ? 'other seats' : 'another seat') + '.',
          });
          return;
        }
        self.executeSwitchSeat(card, switches, isAddSeat);
      })
      .catch(function() {
        self.executeSwitchSeat(card, switches, isAddSeat);
      });
  }

  executeSwitchSeat(card, switches, isAddSeat) {
    var self = this;
    var payWithCard = this.state.payWithCard;
    var user = UserStore.getUser();

    var totalUpgrade = 0;
    var totalDowngrade = 0;
    switches.forEach(function(sw) {
      if (sw.diff > 0) totalUpgrade += sw.diff;
      else if (sw.diff < 0) totalDowngrade += Math.abs(sw.diff);
    });
    var netAmount = totalUpgrade - totalDowngrade;

    var creditToUse = 0;
    var cardPayment = 0;
    if (netAmount > 0) {
      var availableCredit = payWithCard ? 0 : ((user && user.credit) || 0);
      creditToUse = Math.min(availableCredit, netAmount);
      cardPayment = netAmount - creditToUse;
    }

    ReservationService.switchSeat(
      self.props.reservation.reservationNumber,
      self.props.reservation.email,
      switches,
      getSessionId(),
      creditToUse,
      cardPayment,
      card
    )
    .then(function(result) {
      ScheduleService.releaseAllHolds(getSessionId()).catch(function() {});
      eventTracker(isAddSeat ? 'add_seat' : 'switch_seat', 'click', 'Reservations');
      // Build the success summary from the server's response — client-side prices may be stale
      var serverSwitches = (result && result.switches) || [];
      var details = encodeURIComponent(JSON.stringify({
        switches: switches.map(function(sw, i) {
          var srv = serverSwitches[i] || {};
          return {
            oldSeat: srv.oldSeat || sw.old_seat_number,
            newSeat: srv.newSeat || sw.new_seat_number,
            diff: (typeof srv.priceDiff === 'number' ? srv.priceDiff : sw.diff),
          };
        }),
        totalCharged: result && result.totalCharged,
        totalCredited: result && result.totalCredited,
        email: self.props.reservation.email,
      }));
      var resNum = self.props.reservation.reservationNumber;
      window.location.href = '/edit-trip?reservationNumber=' + resNum + '&seatAction=' + (isAddSeat ? 'addSeat' : 'switchSeat') + '&details=' + details;
    })
    .catch(function(err) {
      self.setState({ loading: false, loadingMessage: '', showSeatModal: true, error: friendlyError(err) });
    });
  }

  getStopLabel(stop) {
    return _.find(this.state.cities, { value: stop }).label;
  }

  getStopMetro(stop) {
    return _.find(this.state.cities, { value: stop }).metro;
  }

  getStopPickup(stop) {
    return _.find(this.state.cities, { value: stop }).address;
  }

  getTime(time) {
    return moment(time, "HH:mm:ss").format("hh:mm A");
  }

  handleCount(e) {
    this.setState({ count: e.target.value });
  }

  setSeatsInDropDown() {
    var seats = [];
    for (var i = 1; i <= this.props.reservation.reservations.length; i++) {
      seats.push(i);
    }
    this.setState({ seats: seats, count: 1 });
  }

  findReservation() {
    this.setState({ loading: true, error: '' });
    var criteria = {
      email: this.props.reservation.email,
      reservationNumber: this.props.reservation.reservationNumber
    };
    ReservationService.getByEmail(criteria)
      .then(() => {
        this.setSeatsInDropDown();
      })
      .catch((err) => {
        this.setState({ loading: false, error: err.response.statusText });
      });
  }

  fetchSeatInfo() {
    var scheduleBus = this.props.reservation.schedule_bus;
    var slot = this.props.reservation.reservations[0].slot;

    if (!scheduleBus || !slot) {
      this.setState({ hasSeatNumbers: false });
      this.setSeatsInDropDown();
      return;
    }

    this.setState({ seatInfoLoading: true, seatInfoError: null });

    ReservationService.getByEmail({
      email: this.props.reservation.email,
      reservationNumber: this.props.reservation.reservationNumber
    }).catch(function() {});

    var self = this;
    ScheduleService.getSeatInfo(scheduleBus, slot, getSessionId())
      .then(function(data) {
        var newTaken = (data && data.takenSeats) || [];
        var newTempTaken = (data && data.temporaryTakenSeats) || [];
        var unavailable = newTaken.concat(newTempTaken);

        self.setState(function(prevState) {
          var updates = {
            seatInfoLoading: false,
            busLayout: (data && data.busLayout) || null,
            takenSeats: newTaken,
            temporaryTakenSeats: newTempTaken,
            pricingTiers: (data && data.pricing && data.pricing.tiers) || [],
            maxSelectableRow: (data && data.maxSelectableRow != null) ? data.maxSelectableRow : null,
          };

          // Reconcile switch mappings: clear targets that are now taken/held
          if (prevState.switchStep === 2 && prevState.switchMappings) {
            var newMappings = Object.assign({}, prevState.switchMappings);
            var changed = false;
            Object.keys(newMappings).forEach(function(key) {
              if (newMappings[key] && unavailable.indexOf(newMappings[key]) !== -1) {
                newMappings[key] = null;
                changed = true;
              }
            });
            if (changed) {
              updates.switchMappings = newMappings;
            }
          }

          // Reconcile cancel selections: remove seats no longer in user's active list
          if (prevState.seatsToCancel && prevState.seatsToCancel.length > 0) {
            var userSeats = self.props.reservation.reservations
              .map(function(r) { return r.seat_number; })
              .filter(Boolean);
            var validCancel = prevState.seatsToCancel.filter(function(s) {
              return userSeats.indexOf(s) !== -1;
            });
            if (validCancel.length !== prevState.seatsToCancel.length) {
              updates.seatsToCancel = validCancel;
            }
          }

          return updates;
        });
      })
      .catch(function(_err) {
        // On error, fall back to count mode
        self.setState({
          seatInfoLoading: false,
          seatInfoError: 'Unable to load seat layout. You can still cancel by count.',
          hasSeatNumbers: false
        });
        self.setSeatsInDropDown();
      });
  }

  handleCancelSeatToggle = (seatId) => {
    var userSeats = this.props.reservation.reservations
      .map(function(r) { return r.seat_number; })
      .filter(Boolean);

    if (userSeats.indexOf(seatId) === -1) {
      return;
    }

    this.setState(function(prevState) {
      var seatsToCancel = prevState.seatsToCancel;
      var updated;
      if (seatsToCancel.indexOf(seatId) !== -1) {
        updated = seatsToCancel.filter(function(s) { return s !== seatId; });
      } else {
        updated = seatsToCancel.concat([seatId]);
      }
      return { seatsToCancel: updated, count: updated.length + (prevState.cancelUnassignedCount || 0) };
    });
  }

  getAllSeatIds() {
    var busLayout = this.state.busLayout;
    if (!busLayout || !busLayout.columns || !busLayout.totalRows) {
      return [];
    }
    var allSeatIds = [];
    var leftCols = busLayout.columns.slice(0, busLayout.seatsPerRowLeft);
    var rightCols = busLayout.columns.slice(
      busLayout.seatsPerRowLeft,
      busLayout.seatsPerRowLeft + busLayout.seatsPerRowRight
    );
    var allCols = leftCols.concat(rightCols);
    for (var row = 1; row <= busLayout.totalRows; row++) {
      for (var c = 0; c < allCols.length; c++) {
        allSeatIds.push(String(row) + allCols[c]);
      }
    }
    return allSeatIds;
  }

  proceedToCancel() {
    this.setState({ loading: true, loadingMessage: 'Cancellation in progress...', error: '', success: '' });
    var reason = 'User downgraded ticket';
    var seatNumbers = this.state.hasSeatNumbers ? this.state.seatsToCancel : [];
    var self = this;
    ReservationService.cancelSeats(
      this.props.reservation.reservationNumber,
      this.props.reservation.email,
      this.state.count,
      reason,
      this.props.reservation.reservationGroup,
      seatNumbers
    )
      .then(function(result) {
        eventTracker('cancel_ticket', 'click', 'Reservations');
        var totalTickets = self.props.reservation.reservations.length;
        var cancelledCount = self.state.count;
        self.props.clearReservation(self.props.reservation.email);
        self.props.setRefund(result.refund, result.cancelledSeats, cancelledCount, totalTickets);
      })
      .catch(function(err) {
        self.setState({ loading: false, loadingMessage: '', error: friendlyError(err) });
      });
  }

  rescheduleSeats() {
    var cancelUnassignedCount = this.state.cancelUnassignedCount || 0;
    var assignedCount = this.state.hasSeatNumbers ? this.state.seatsToCancel.length : 0;
    var count = this.state.hasSeatNumbers ? (assignedCount + cancelUnassignedCount) : this.state.count;
    if (!count || count < 1) {
      return;
    }
    var amount = 0, points = 0;
    var reservations = this.props.reservation.reservations || [];
    var seatsToCancel = this.state.seatsToCancel || [];
    var activeRes = reservations.filter(function(r) { return r.status === 'active'; });
    if (this.state.hasSeatNumbers) {
      for (var i = 0; i < assignedCount; i++) {
        var res = reservations.find(function(r) { return r.seat_number === seatsToCancel[i]; });
        if (!res) res = activeRes[i];
        if (!res) continue;
        amount += (res.credit || 0) + (res.paid || 0);
        points += (res.vouchers || 0);
      }
      var unassignedRes = activeRes.filter(function(r) { return !r.seat_number; });
      for (var j = 0; j < cancelUnassignedCount && j < unassignedRes.length; j++) {
        amount += (unassignedRes[j].credit || 0) + (unassignedRes[j].paid || 0);
        points += (unassignedRes[j].vouchers || 0);
      }
    } else {
      for (var k = 0; k < count && k < activeRes.length; k++) {
        amount += (activeRes[k].credit || 0) + (activeRes[k].paid || 0);
        points += (activeRes[k].vouchers || 0);
      }
    }
    this.props.clearReservation(this.props.reservation.email);
    var oldSeatNumbers = this.state.hasSeatNumbers ? seatsToCancel.join(',') : '';
    browserHistory.push('/?reservationNumber=' + this.props.reservation.reservationNumber + '&busType=' + this.props.reservation.bus_type + '&seats=' + count + '&oldSeatNumbers=' + encodeURIComponent(oldSeatNumbers) + '&oldUnassignedCount=' + cancelUnassignedCount + '&departCity=' + this.props.reservation.reservations[0].depart_stop.city + '&arriveCity=' + this.props.reservation.reservations[0].arrive_stop.city + '&amount=' + amount + '&points=' + points + '&price=' + this.props.reservation.reservations[0].order_item.amount);
  }

  cancelSeats(_e) {
    confirmAlert({
      title: 'Confirm to cancel',
      message: 'Are you sure to do this?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => this.proceedToCancel()
        },
        {
          label: 'No',
          onClick: () => ''
        }
      ]
    });
  }

  componentDidMount() {
    if (this.state.hasSeatNumbers) {
      this.fetchSeatInfo();
    } else {
      this.setSeatsInDropDown();
    }
  }

  renderSeatModal() {
    var action = this.state.modalAction;
    var busLayout = this.state.busLayout;
    var seatInfoLoading = this.state.seatInfoLoading;
    var seatsToCancel = this.state.seatsToCancel;

    var userSeats = this.props.reservation.reservations
      .map(function(r) { return r.seat_number; })
      .filter(Boolean);

    // Switch seat / Add seat flow
    if (action === 'switch' || action === 'addSeat') {
      return this.renderSwitchSeatModal(userSeats, busLayout, seatInfoLoading);
    }

    var isCancel = action === 'cancel';
    var isCancelAddon = action === 'cancelAddon';
    var actionVerb = isCancelAddon ? 'remove selection from' : (isCancel ? 'cancel' : 'reschedule');
    var actionLabel = isCancelAddon ? 'Remove Selection' : (isCancel ? 'Cancel' : 'Reschedule');

    var allReservations = this.props.reservation.reservations || [];
    var unassignedCount = allReservations.filter(function(r) { return !r.seat_number; }).length;
    var cancelUnassignedCount = this.state.cancelUnassignedCount || 0;
    var self = this;

    var body;
    if (seatInfoLoading) {
      body = (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading seat layout...</p>
        </div>
      );
    } else if (!busLayout || !busLayout.columns || !busLayout.totalRows) {
      body = (
        <div className="error-container">
          <p className="error-message">Unable to load seat layout.</p>
          <button className="btn-secondary" onClick={this.closeSeatModal.bind(this)}>Close</button>
        </div>
      );
    } else {
      var allSeatIds = this.getAllSeatIds();
      var cancelModeTakenSeats = allSeatIds.filter(function(id) {
        return userSeats.indexOf(id) === -1;
      });
      var tiers = this.state.pricingTiers || [];
      var refundTotal = isCancelAddon ? seatsToCancel.reduce(function(sum, s) {
        var res = allReservations.find(function(r) { return r.seat_number === s; });
        return sum + paidSeatValue(res, s, tiers);
      }, 0) : 0;

      body = (
        <div>
          <p className="instruction-text">
            {userSeats.length > 0
              ? 'Tap your seats (shown in green) to select them for ' + actionVerb + '.'
              : 'You have ' + unassignedCount + ' unassigned ticket(s).'}
          </p>
          {isCancelAddon && refundTotal > 0 && <p className="instruction-text"><strong>Credit refund: ${refundTotal.toFixed(2)}</strong></p>}
          {userSeats.length > 0 && (
            <div className="seat-map-container">
              <SeatMap
                busLayout={busLayout}
                selectedSeats={seatsToCancel}
                takenSeats={cancelModeTakenSeats}
                temporaryTakenSeats={[]}
                basePrice={0}
                pricingTiers={[]}
                maxSeats={userSeats.length}
                showSeatId={true}
                onSeatSelect={this.handleCancelSeatToggle}
              />
              <div className="legend-container">
                <div className="seat-legend">
                  <div className="legend-title">Legend</div>
                  <div className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: '#4CAF50' }} />
                    <span className="legend-label">Your Seat</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: '#e8512f' }} />
                    <span className="legend-label">Selected to {actionLabel}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: '#9E9E9E' }} />
                    <span className="legend-label">Other / Unavailable</span>
                  </div>
                </div>
                {(isCancel || action === 'reschedule') && unassignedCount > 0 && (
                  <div style={{ marginTop: '14px', padding: '10px', background: '#f8f8f8', borderRadius: '6px', fontSize: '13px' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 'bold' }}>Unassigned Tickets ({unassignedCount})</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ margin: 0 }}>{isCancel ? 'Cancel:' : 'Include:'}</label>
                      <select
                        value={cancelUnassignedCount}
                        onChange={function(e) {
                          var val = parseInt(e.target.value, 10);
                          self.setState(function(prevState) {
                            return { cancelUnassignedCount: val, count: prevState.seatsToCancel.length + val };
                          });
                        }}
                        style={{ width: '50px', padding: '3px 4px', borderRadius: '4px', border: '1px solid #ccc' }}
                      >
                        {Array.from({ length: unassignedCount + 1 }, function(_, i) { return i; }).map(function(n) {
                          return <option key={n} value={n}>{n}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    var totalSelected = seatsToCancel.length + cancelUnassignedCount;
    var maxTotal = userSeats.length + ((isCancel || action === 'reschedule') ? unassignedCount : 0);

    return (
      <div className="seat-selection-modal" onClick={this.closeSeatModal.bind(this)}>
        <div className="seat-selection-dialog" onClick={function(e) { e.stopPropagation(); }}>
          <div className="seat-selection-content">
            <div className="seat-selection-header">
              <div className="header-left">
                <h2 className="header-title">
                  Select seat(s) to {actionVerb}
                  <span className="max-seats-indicator">(up to {maxTotal})</span>
                </h2>
              </div>
              <div className="header-right">
                <span className="selected-count">
                  {totalSelected} selected
                </span>
                <button type="button" className="btn-close" onClick={this.closeSeatModal.bind(this)} aria-label="Close">
                  <span style={{ fontSize: '24px', lineHeight: 1 }}>&times;</span>
                </button>
              </div>
            </div>
            <div className="seat-selection-body">
              {body}
            </div>
            <div className="seat-selection-footer">
              <div className="total-section">
                {totalSelected > 0 && (
                  <span className="total-label">
                    Selected: <strong>
                      {seatsToCancel.length > 0 ? seatsToCancel.join(', ') : ''}
                      {seatsToCancel.length > 0 && cancelUnassignedCount > 0 ? ' + ' : ''}
                      {cancelUnassignedCount > 0 ? cancelUnassignedCount + ' unassigned' : ''}
                    </strong>
                  </span>
                )}
              </div>
              {this.state.error && <div className="error" style={{ marginBottom: '8px', color: '#d32f2f', fontSize: '13px' }}>{this.state.error}</div>}
              <div className="action-buttons">
                <button type="button" className="btn-skip" onClick={this.closeSeatModal.bind(this)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-confirm"
                  disabled={totalSelected === 0 || this.state.loading}
                  onClick={this.confirmFromModal.bind(this)}
                >
                  {actionLabel}{totalSelected > 0 ? ' ' + totalSelected + ' ticket(s)' : ''}
                </button>
                {isCancel && maxTotal > 1 && (
                  <button
                    type="button"
                    className="btn-confirm"
                    style={{ backgroundColor: '#dc3545', marginLeft: '8px' }}
                    disabled={this.state.loading}
                    onClick={function() {
                      var allSeats = userSeats.slice();
                      var allCount = allSeats.length + unassignedCount;
                      self.setState({ seatsToCancel: allSeats, cancelUnassignedCount: unassignedCount, count: allCount, showSeatModal: false, modalAction: null }, function() {
                        self.cancelSeats();
                      });
                    }}
                  >
                    Cancel Entire Reservation
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSwitchSeatModal(userSeats, busLayout, seatInfoLoading) {
    var self = this;
    var isAddSeat = this.state.modalAction === 'addSeat';
    var tiers = this.state.pricingTiers || [];
    var currentUser = UserStore.getUser();
    var availableCredit = (currentUser && currentUser.credit) || 0;
    var seatsToSwitch = this.state.seatsToSwitch;
    var mappings = this.state.switchMappings;
    var isStep2 = this.state.switchStep === 2;

    var allSeatIds = busLayout ? this.getAllSeatIds() : [];
    var maxRow = this.state.maxSelectableRow;
    var selectableSeatIds = maxRow != null
      ? allSeatIds.filter(function(id) { var m = id.match(/^(\d+)/); return m && parseInt(m[1], 10) <= maxRow; })
      : allSeatIds;
    var switchUnavailable = (this.state.takenSeats || []).concat(this.state.temporaryTakenSeats || []);
    var availableSelectableCount = selectableSeatIds.filter(function(id) { return switchUnavailable.indexOf(id) === -1 && userSeats.indexOf(id) === -1; }).length;
    var exceedsAvailable = seatsToSwitch.length > 0 && seatsToSwitch.length > availableSelectableCount;

    // Step 1: only user's seats are interactive (others grayed out)
    // Step 2: only available seats are interactive (user's seats + taken are grayed)
    var seatMapContent;
    if (!busLayout) {
      seatMapContent = <div className="error-container"><p className="error-message">Unable to load seat layout.</p></div>;
    } else if (!isStep2) {
      var otherSeats = allSeatIds.filter(function(id) { return userSeats.indexOf(id) === -1; });
      seatMapContent = (
        <SeatMap busLayout={busLayout} selectedSeats={seatsToSwitch} takenSeats={otherSeats} temporaryTakenSeats={[]} basePrice={0} pricingTiers={tiers} maxSeats={userSeats.length} maxSelectableRow={this.state.maxSelectableRow} showSeatId={true} onSeatSelect={this.handleSwitchSelectToggle} />
      );
    } else {
      var currentSeat = seatsToSwitch[this.state.switchActiveIndex] || null;
      var mappedTargets = [];
      Object.keys(mappings).forEach(function(k) { if (mappings[k]) mappedTargets.push(mappings[k]); });
      var baseTaken = this.state.takenSeats || [];
      var takenWithUserSeats = baseTaken.slice();
      userSeats.forEach(function(s) { if (takenWithUserSeats.indexOf(s) === -1) takenWithUserSeats.push(s); });
      seatMapContent = (
        <SeatMap busLayout={busLayout} selectedSeats={mappedTargets} pendingSeats={this.state.switchPendingSeats} pendingReleaseSeats={this.state.switchPendingReleaseSeats} takenSeats={takenWithUserSeats} temporaryTakenSeats={this.state.temporaryTakenSeats || []} basePrice={0} pricingTiers={tiers} maxSeats={seatsToSwitch.length} maxSelectableRow={this.state.maxSelectableRow} showSeatId={false} onSeatSelect={this.handleSwitchMapSeat} />
      );
    }

    // Calculate totals for step 2
    var allReservationsForDiff = this.props.reservation.reservations || [];
    var totalDiff = 0;
    var allMapped = isStep2 && seatsToSwitch.every(function(s) { return mappings[s]; });
    if (isStep2) {
      seatsToSwitch.forEach(function(s) {
        if (mappings[s]) {
          var oldRes = allReservationsForDiff.find(function(r) { return r.seat_number === s; });
          var oldPrice = paidSeatValue(oldRes, s, tiers);
          totalDiff += getSeatTierAddon(mappings[s], tiers) - oldPrice;
        }
      });
    }
    var useCredits = !this.state.payWithCard && availableCredit > 0;
    var creditApplied = (totalDiff > 0 && useCredits) ? Math.min(availableCredit, totalDiff) : 0;
    var cardPaymentNeeded = totalDiff > 0 ? totalDiff - creditApplied : 0;

    // Side note content
    // Payment panel — rendered full-width below the seat map; the modal body scrolls so the map keeps its size
    var paymentPanel = null;
    if (allMapped && totalDiff > 0) {
      paymentPanel = (
        <div style={{flexShrink: 0, padding: '0 15px 10px'}}>
        <div style={{padding: '10px', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee'}}>
          <p style={{margin: '0 0 8px', fontSize: '13px'}}>
            Total upgrade cost: <strong>${totalDiff.toFixed(2)}</strong>
          </p>
          {availableCredit > 0 && (
            <div style={{marginBottom: '8px'}}>
              <label style={{display: 'block', fontSize: '13px', cursor: 'pointer', marginBottom: '4px'}}>
                <input type="radio" name="paymentMethod" checked={!self.state.payWithCard} onChange={function() { self.setState({ payWithCard: false, card_cardHolder_error: '', card_cardNumber_error: '', card_cardMonth_error: '', card_cardYear_error: '', card_cardCvv_error: '', card_cardZip_error: '' }); }} style={{marginRight: '6px'}} />
                Use Credits {creditApplied >= totalDiff ? <span style={{color: '#4CAF50', fontWeight: 'bold'}}>(fully covered)</span> : <span>(${availableCredit.toFixed(2)} available)</span>}
              </label>
              <label style={{display: 'block', fontSize: '13px', cursor: 'pointer'}}>
                <input type="radio" name="paymentMethod" checked={self.state.payWithCard} onChange={function() { self.setState({ payWithCard: true }); }} style={{marginRight: '6px'}} />
                Pay with Credit Card
              </label>
            </div>
          )}
          {!self.state.payWithCard && creditApplied > 0 && (
            <p style={{margin: '0 0 4px', fontSize: '13px', color: '#4CAF50'}}>
              Credit applied: -${creditApplied.toFixed(2)}
            </p>
          )}
          {cardPaymentNeeded > 0 ? (
            <div>
              <p style={{margin: '0 0 8px', fontSize: '13px', fontWeight: 'bold'}}>
                Card charge: ${cardPaymentNeeded.toFixed(2)}
              </p>
              <div style={{display: 'flex', gap: '4px', marginBottom: '4px'}}>
                <div style={{flex: 1}}>
                  <input name="cardHolder" required onBlur={this.handleCardBlur.bind(this)} value={this.state.cardHolder} onChange={this.handleCardChange.bind(this)} type="text" className={'form-control' + (this.state.card_cardHolder_error ? ' error' : '')} placeholder="Name On Card" />
                  {this.state.card_cardHolder_error && <div className="error">{this.state.card_cardHolder_error}</div>}
                </div>
                <div style={{flex: 1}}>
                  <InputMask required maskChar='' mask="9999-9999-9999-9999" name="cardNumber" onBlur={this.handleCardBlur.bind(this)} onChange={this.handleCardChange.bind(this)} value={this.state.cardNumber} className={'form-control' + (this.state.card_cardNumber_error ? ' error' : '')} placeholder="Credit Card Number" />
                  {this.state.card_cardNumber_error && <div className="error">{this.state.card_cardNumber_error}</div>}
                </div>
              </div>
              <div style={{display: 'flex', gap: '4px'}}>
                <div style={{flex: 1}}>
                  <select name="cardMonth" required onBlur={this.handleCardBlur.bind(this)} value={this.state.cardMonth} onChange={this.handleCardChange.bind(this)} className={'form-control' + (this.state.card_cardMonth_error ? ' error' : '')}>
                    <option value="">MM</option>
                    {['01','02','03','04','05','06','07','08','09','10','11','12'].map(function(m) { return <option key={m} value={m}>{m}</option>; })}
                  </select>
                  {this.state.card_cardMonth_error && <div className="error">{this.state.card_cardMonth_error}</div>}
                </div>
                <div style={{flex: 1}}>
                  <select name="cardYear" required onBlur={this.handleCardBlur.bind(this)} value={this.state.cardYear} onChange={this.handleCardChange.bind(this)} className={'form-control' + (this.state.card_cardYear_error ? ' error' : '')}>
                    <option value="">YY</option>
                    {Array.from({length: 10}, function(_, i) { return String(new Date().getFullYear() + i).slice(-2); }).map(function(y) { return <option key={y} value={y}>{y}</option>; })}
                  </select>
                  {this.state.card_cardYear_error && <div className="error">{this.state.card_cardYear_error}</div>}
                </div>
                <div style={{flex: 1}}>
                  <InputMask required maskChar='' mask="9999" name="cardCvv" onBlur={this.handleCardBlur.bind(this)} onChange={this.handleCardChange.bind(this)} value={this.state.cardCvv} className={'form-control' + (this.state.card_cardCvv_error ? ' error' : '')} placeholder="SEC" />
                  {this.state.card_cardCvv_error && <div className="error">{this.state.card_cardCvv_error}</div>}
                </div>
                <div style={{flex: 1}}>
                  <input inputMode="numeric" pattern="(\d{5}([\-]\d{4})?)" name="cardZip" required maxLength={5} onBlur={this.handleCardBlur.bind(this)} value={this.state.cardZip} onChange={this.handleCardChange.bind(this)} type="text" className={'form-control' + (this.state.card_cardZip_error ? ' error' : '')} placeholder="ZIP" />
                  {this.state.card_cardZip_error && <div className="error">{this.state.card_cardZip_error}</div>}
                </div>
              </div>
            </div>
          ) : (
            <p style={{margin: 0, fontSize: '13px', color: '#4CAF50', fontWeight: 'bold'}}>
              Fully covered by credit - no card needed
            </p>
          )}
        </div>
        </div>
      );
    }

    var sideNote;
    if (!isStep2) {
      sideNote = seatsToSwitch.length > 0 ? (
        <div className="switch-seat-sidebar" style={{fontSize: '13px', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
          <div style={{background: '#f5f5f5', borderRadius: '6px', padding: '10px'}}>
            <div style={{fontWeight: 'bold', marginBottom: '4px'}}>Selected</div>
            <p style={{margin: 0}}>{seatsToSwitch.join(', ')}</p>
          </div>
          {exceedsAvailable && (
            <div style={{marginTop: '8px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: '4px', padding: '8px', fontSize: '12px', color: '#e65100'}}>
              &#9888; Only {availableSelectableCount} selectable seat{availableSelectableCount !== 1 ? 's' : ''} available. Select fewer seats or cancel and rebook.
            </div>
          )}
          <button type="button" disabled={exceedsAvailable} style={{marginTop: '12px', width: '100%', backgroundColor: exceedsAvailable ? '#ccc' : '#e8512f', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '4px', fontWeight: '600', fontSize: '14px', cursor: exceedsAvailable ? 'not-allowed' : 'pointer'}} onClick={function() {
            if (exceedsAvailable) return;
            var initMappings = {};
            self.state.seatsToSwitch.forEach(function(s) { initMappings[s] = null; });
            self.setState(function(prev) { return { switchStep: 2, switchMappings: initMappings, switchActiveIndex: 0, holdTimerKey: prev.holdTimerKey + 1 }; }, function() { self.fetchSeatInfo(); });
          }}>
            Next
          </button>
        </div>
      ) : null;
    } else {
      var currentSeatForNote = seatsToSwitch[this.state.switchActiveIndex] || null;
      sideNote = (
        <div className="switch-seat-sidebar" style={{fontSize: '13px'}}>
          <div style={{marginBottom: '14px'}}>
            <div style={{fontWeight: 'bold', marginBottom: '6px'}}>Legend</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px'}}>
              <div style={{width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#4CAF50'}} />
              <span>Available</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px'}}>
              <div style={{width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#e8512f'}} />
              <span>Selected</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px'}}>
              <div style={{width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#9E9E9E'}} />
              <span>Taken</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: self.state.maxSelectableRow != null ? '4px' : '0'}}>
              <div style={{width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#7B1FA2'}} />
              <span>Held</span>
            </div>
            {self.state.maxSelectableRow != null && (
              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                <div style={{width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#BDBDBD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', color: '#757575'}}>&times;</div>
                <span>First come first serve</span>
              </div>
            )}
          </div>
          <div style={{background: '#f5f5f5', borderRadius: '6px', padding: '10px'}}>
            <div style={{fontWeight: 'bold', marginBottom: '6px'}}>{isAddSeat ? 'Seat Selections' : 'Switch Mappings'}</div>
            {seatsToSwitch.map(function(s, i) {
              var target = mappings[s];
              if (isAddSeat && !target) return null;
              var isActive = i === self.state.switchActiveIndex;
              var addonPrice = target ? getSeatTierAddon(target, tiers) : 0;
              var oldRes = allReservationsForDiff.find(function(r) { return r.seat_number === s; });
              var oldPrice = paidSeatValue(oldRes, s, tiers);
              var diff = target ? addonPrice - oldPrice : 0;
              var displayLabel = isAddSeat ? 'Seat ' + (i + 1) : s;
              return (
                <div key={s} style={{
                  padding: '4px 6px', marginBottom: '4px', borderRadius: '4px', cursor: 'pointer',
                  background: isActive ? '#fff' : 'transparent',
                  border: isActive ? '1px solid #e8512f' : '1px solid transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }} onClick={function() { self.setState({ switchActiveIndex: i }); }}>
                  <span>
                    {isAddSeat ? (
                      <span><strong>{displayLabel}</strong>: <strong>{target}</strong>
                        {addonPrice > 0 && <span style={{color: '#e8512f', marginLeft: '4px'}}>+${addonPrice.toFixed(2)}</span>}
                        {addonPrice === 0 && <span style={{color: '#888', marginLeft: '4px'}}>$0</span>}
                      </span>
                    ) : (
                      <span><strong>{displayLabel}</strong> <span style={{color: '#888', fontSize: '12px'}}>(paid ${oldPrice.toFixed(2)})</span> &rarr; {target ? <strong>{target}</strong> : <span style={{color: '#999'}}>?</span>}
                        {target && diff > 0 && <span style={{color: '#e8512f', marginLeft: '4px'}}>+${diff.toFixed(2)}</span>}
                        {target && diff < 0 && <span style={{color: '#4CAF50', marginLeft: '4px'}}>-${Math.abs(diff).toFixed(2)}</span>}
                        {target && diff === 0 && <span style={{color: '#888', marginLeft: '4px'}}>$0</span>}
                      </span>
                    )}
                  </span>
                  {target && (
                    <span onClick={function(e) { e.stopPropagation(); self.removeSwitchMapping(s); }} style={{color: '#999', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px', marginLeft: '6px'}} title="Remove">&times;</span>
                  )}
                </div>
              );
            })}
            {allMapped && totalDiff !== 0 && (
              <div style={{borderTop: '1px solid #ddd', marginTop: '6px', paddingTop: '6px'}}>
                <strong>Total: </strong>
                {totalDiff > 0 && <span style={{color: '#e8512f'}}>+${totalDiff.toFixed(2)}</span>}
                {totalDiff < 0 && <span style={{color: '#4CAF50'}}>-${Math.abs(totalDiff).toFixed(2)} credit</span>}
              </div>
            )}
          </div>
        </div>
      );
    }

    var instruction = '';
    if (isAddSeat) {
      var activeIdx = this.state.switchActiveIndex;
      instruction = allMapped
        ? 'All seats selected. Review and confirm.'
        : 'Pick a seat for Seat ' + (activeIdx + 1) + '.';
    } else if (!isStep2) {
      instruction = 'Select the seat(s) you want to switch, then press Next.';
    } else {
      var activeSeat = seatsToSwitch[this.state.switchActiveIndex];
      instruction = activeSeat
        ? 'Pick a new seat for ' + activeSeat + '. Click a mapping on the right to change it.'
        : 'All seats mapped. Review and confirm.';
    }

    var modalTitle = isAddSeat ? 'Select Seat' + (seatsToSwitch.length > 1 ? 's' : '') : 'Switch Seat' + (isStep2 ? 's' : '');

    return (
      <div className="seat-selection-modal" onClick={this.closeSeatModal.bind(this)}>
        <div className="seat-selection-dialog" onClick={function(e) { e.stopPropagation(); }} style={{maxWidth: '680px'}}>
          <div className="seat-selection-content" style={{display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflowY: 'auto'}}>
            <div className="seat-selection-header">
              <div className="header-left">
                <h2 className="header-title">{modalTitle}</h2>
                <p style={{margin: '2px 0 0', fontSize: '13px', color: '#fff', fontWeight: 'bold'}}>{instruction}</p>
              </div>
              <div className="header-right" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                {isStep2 && <HoldTimer key={this.state.holdTimerKey} durationSeconds={600} onExpired={this.handleHoldTimerExpired} />}
                <span style={{fontSize: '13px', color: '#fff', fontWeight: 'bold'}}>
                  Credit: ${availableCredit.toFixed(2)}
                </span>
                <button type="button" className="btn-close" onClick={this.closeSeatModal.bind(this)} aria-label="Close">
                  <span style={{ fontSize: '24px', lineHeight: 1 }}>&times;</span>
                </button>
              </div>
            </div>
            {seatInfoLoading && !busLayout ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading seat layout...</p>
              </div>
            ) : (
            <div className="switch-seat-body" style={{gap: '12px', padding: '10px 15px', minHeight: '340px'}}>
              <div style={{flex: '1 1 280px', overflowY: 'auto', minHeight: 0, padding: '10px 10px 0 10px'}}>
                {isStep2 && !isAddSeat && (
                  <div style={{marginBottom: '8px', display: 'flex', gap: '6px'}}>
                    <button type="button" onClick={function() { ScheduleService.releaseAllHolds(getSessionId()).catch(function() {}); self.setState(function(prev) { return { switchStep: 1, switchMappings: {}, switchActiveIndex: 0, switchPendingSeats: [], switchPendingReleaseSeats: [], error: '', holdTimerKey: prev.holdTimerKey + 1 }; }); self.fetchSeatInfo(); }} style={{background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: '#666'}}>
                      &#8635; Restart
                    </button>
                    <button type="button" onClick={function() { ScheduleService.releaseAllHolds(getSessionId()).catch(function() {}); var initMappings = {}; seatsToSwitch.forEach(function(s) { initMappings[s] = null; }); self.setState({ switchMappings: initMappings, switchActiveIndex: 0, switchPendingSeats: [], switchPendingReleaseSeats: [], error: '' }); self.fetchSeatInfo(); }} style={{background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: '#666'}}>
                      &#x21bb; Refresh
                    </button>
                  </div>
                )}
                <div style={{position: 'relative'}}>
                  {seatInfoLoading && busLayout && (
                    <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '4px'}}>
                      <div className="loading-spinner"></div>
                      <p style={{marginTop: '10px', color: '#888', fontSize: '13px'}}>Refreshing...</p>
                    </div>
                  )}
                  {seatMapContent}
                </div>
              </div>
              {sideNote}
            </div>
            )}
            {paymentPanel}
            {allMapped && totalDiff < 0 && (
              <div style={{flexShrink: 0, padding: '0 15px 10px'}}>
                <p style={{margin: 0, fontSize: '13px', color: '#4CAF50'}}>
                  ${Math.abs(totalDiff).toFixed(2)} will be added to your credit balance.
                </p>
              </div>
            )}
            {this.state.error && (
              <div style={{flexShrink: 0, padding: '0 15px 8px'}}>
                <div className="error">{this.state.error}</div>
              </div>
            )}
            <div className="seat-selection-footer" style={{flexShrink: 0}}>
              <div className="action-buttons">
                {isStep2 && allMapped && this.state.switchPendingSeats.length === 0 && this.state.switchPendingReleaseSeats.length === 0 && (
                  <button type="button" className="btn-confirm" disabled={this.state.loading} onClick={this.proceedSwitchSeat.bind(this)}>
                    {this.state.loading ? 'Processing, please wait...' : (isAddSeat ? 'Confirm Selection' : 'Confirm Switch')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCountPicker() {
    return (
      <div className="ticketCancelChangeBox">
        <p>HOW MANY TICKETS DO YOU WANT TO CHANGE/CANCEL</p>
        <div className="row">
          <div className="col-md-2 form-group">
            <img src={personIcon} alt="person icon" className="chairIcon" width="18px" height="18px" />
            <select onChange={this.handleCount.bind(this)} name="count" value={this.state.count} className="form-control" id="inlineFormInputGroup">
              {this.state.seats.map(function(item, index) {
                return (
                  <option key={index}>{item}</option>
                );
              })}
            </select>
          </div>
          <div className="col-md-3 form-group">
            <button type="button" className="btn btn-block btn-blue cancel-btn" disabled={!this.state.count || this.state.loading} onClick={this.cancelSeats.bind(this)}>CANCEL</button>
          </div>
          <div className="col-md-3 form-group">
            <button type="button" className="btn btn-block btn-blue" disabled={!this.state.count || this.state.loading} onClick={this.rescheduleSeats.bind(this)}>RESCHEDULE</button>
          </div>
          <div className="col-md-4 form-group">
            <button type="button" className="btn btn-block btn-blue" disabled={this.state.loading} onClick={this.handleAddSeatClick.bind(this)}>SELECT SEAT</button>
          </div>
        </div>
        {this.state.loadingMessage && <p style={{ color: '#e8512f', fontWeight: '600', margin: '8px 0 0' }}>{this.state.loadingMessage}</p>}
        {this.state.error && <div className="error">{this.state.error}</div>}
        {this.state.success && <div className="sucess">{this.state.success}</div>}
      </div>
    );
  }

  render() {
    var reservation = this.props.reservation;
    var hasSeatNumbers = this.state.hasSeatNumbers;

    return (
      reservation && <div className="container editTrip confirmTrip">
        <div className="editTripBox">
          <div className="header-content">
            <h4>We have found your reservation!</h4>
            <p>Reservation #{config.formatReservationNumber(reservation.reservationNumber)}</p>
          </div>
          <div>
            <p>You currently have <span className="bold">{reservation.reservations.length}</span> ticket/s.
              {hasSeatNumbers
                ? ' Click cancel or reschedule below to pick which seat(s) to change.'
                : ' Select how many you\'d like to edit/cancel.'}
            </p>
            <div className="row tripDetails">
              <div className="col-sm-6 departingTrip">
                <p className="tripType">Reservation Details:</p>
                <p className="tripAddress">{this.getStopLabel(reservation.reservations[0].depart_stop.city)} to {this.getStopLabel(reservation.reservations[0].arrive_stop.city)}</p>
                <p>{moment(reservation.reservations[0].slot, "YYYY-MM-DD").format('MMM D, YYYY')} at <span className="bold">{moment(reservation.reservations[0].depart_stop.time, 'HH:mm:ss').format('h:mm a')}</span></p>
                <div className="pickUp">
                  <p>Pick up:</p>
                  <p className="gray-text">{reservation.reservations[0].depart_stop.address}</p>
                  <p className="description">{reservation.reservations[0].depart_stop.description}</p>
                </div>
                <div className="dropOff">
                  <p>Drop off:</p>
                  <p className="gray-text">{reservation.reservations[0].arrive_stop.address}</p>
                  <p className="description">{reservation.reservations[0].arrive_stop.drop_off_description || reservation.reservations[0].arrive_stop.description}</p>
                </div>
              </div>
              <div className="col-sm-6 callUs">
                <p>If you have questions</p>
                <h5>Call us at  <a href="tel:+17188349214">718-834-9214</a></h5>
              </div>
            </div>

            {hasSeatNumbers ? (
              <div className="ticketCancelChangeBox">
                <div className="row">
                  <div className="col-md-3 form-group">
                    <button
                      type="button"
                      className="btn btn-block btn-blue cancel-btn"
                      disabled={this.state.loading}
                      onClick={this.handleCancelClick.bind(this)}
                    >
                      CANCEL
                    </button>
                  </div>
                  <div className="col-md-3 form-group">
                    <button
                      type="button"
                      className="btn btn-block btn-blue"
                      disabled={this.state.loading}
                      onClick={this.handleRescheduleClick.bind(this)}
                    >
                      RESCHEDULE
                    </button>
                  </div>
                  <div className="col-md-3 form-group">
                    <button
                      type="button"
                      className="btn btn-block btn-blue"
                      disabled={this.state.loading}
                      onClick={this.handleSwitchSeatClick.bind(this)}
                    >
                      SWITCH SEAT
                    </button>
                  </div>
                  <div className="col-md-3 form-group">
                    <button
                      type="button"
                      className="btn btn-block btn-blue"
                      disabled={this.state.loading}
                      onClick={this.handleCancelAddonClick.bind(this)}
                    >
                      REMOVE SEAT
                    </button>
                  </div>
                </div>
                {(() => {
                  var unassigned = reservation.reservations.filter(function(r) { return !r.seat_number; }).length;
                  if (!unassigned) return null;
                  return (
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
                      You have {unassigned} seat{unassigned > 1 ? 's' : ''} unassigned. <a href="javascript:void(0)" onClick={this.handleAddSeatClick.bind(this)} style={{ color: '#e8512f', fontWeight: '600' }}>Click to assign</a>
                    </p>
                  );
                })()}
                {this.state.loadingMessage && <p style={{ color: '#e8512f', fontWeight: '600', margin: '8px 0 0' }}>{this.state.loadingMessage}</p>}
                {this.state.error && <div className="error">{this.state.error}</div>}
                {this.state.success && <div className="sucess">{this.state.success}</div>}
              </div>
            ) : (
              this.renderCountPicker()
            )}
          </div>
        </div>
        {this.state.showSeatModal && this.renderSeatModal()}
        {this.state.sessionExpired && <SessionExpiredPopup onStartAgain={this.handleSessionRestart} />}
      </div>
    );
  }
}

export default Reservation;
